import React, { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { components as selectComponents } from 'react-select';
import Select from '~components/Form/components/Select';
import Info from '~components/Form/components/Info';
import { db } from '~/db/db';
import { ModalClosed } from '~misc/icons';

import { formatCurrency, formatDias, onlyDigits } from '../../helpers/TrackMapping';
import useFunilConcorrentes from '~fetchs/Funnel/useFunilConcorrentes';
import useFunilObraMotivoPerda from '~fetchs/Funnel/useFunilObraMotivoPerda';

const inputCls =
  'h-12 px-3 rounded-md mb-3 border border-[#cbd5e0] bg-white text-sm text-primaria font-semibold focus:outline-none w-full';

const Field = ({ label, tooltip, children }) => (
  <div className="flex flex-col gap-2">
    <label className="text-sm font-bold text-primaria flex items-center gap-1">
      {label}
      {tooltip && <Info bg="blue" position="mobile-left" text={tooltip} tooltipClass="w-6 h-6 relative" />}
    </label>
    {children}
  </div>
);

const schema = yup.object({
  funilObraMotivoPerdaId: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value
    )
    .nullable()
    .required('Selecione um motivo de perda'),

  funilObraConcorrenteId: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value
    )
    .nullable()
    .required('Selecione um concorrente'),
});

const LostMappingModal = ({ onSubmit, onClose, isOpen }) => {
  const [data, setData] = useState({
    funilObraConcorrenteId: "",
    funilObraMotivoPerdaId: "",
    precoNecessario: "",
    prazoPagamento: ""
  })
  const [precoNecessario, setPreco] = useState('');
  const [prazoPagamento, setPrazo] = useState('');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const { optionFunilConcorrentes } = useFunilConcorrentes({  });
  const { optionFunilObraMotivosPerda } = useFunilObraMotivoPerda({  });

  if (!isOpen) return null;

  const reset = () => {
    setPreco('');
    setPrazo('');
    setErrors({});
    setTouched({});
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async () => {
    try {
      await schema.validate(data,
        { abortEarly: false },
      );

      setErrors({});

      onSubmit?.({
        funilObraMotivoPerdaId: data.funilObraMotivoPerdaId,
        funilObraConcorrenteId: data.funilObraConcorrenteId,
        precoNecessario,
        prazoPagamento: onlyDigits(prazoPagamento),
      });

      handleClose();
    } catch (err) {
      console.log(err);
      const formatted = {};
      const touchedMap = {};

      err.inner?.forEach((e) => {
        formatted[e.path] = e.message;
        touchedMap[e.path] = true;
      });

      setErrors(formatted);
      setTouched(touchedMap);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center">
      <div className="bg-white px-8 py-8 rounded-[20px] shadow-lg relative w-[600px]">
        <button
          type="button"
          onClick={handleClose}
          className="text-white p-3 rounded-tr-[20px] bg-quinaria absolute top-0 right-0"
        >
          <ModalClosed />
        </button>
        <div className="my-4">
          <Field label="Motivo de Perda*:">
            <Select
              options={optionFunilObraMotivosPerda}
              value={optionFunilObraMotivosPerda.find(e => e.value == data.funilObraMotivoPerdaId)}
              onChange={(e) => {
                setData(prev => ({
                  ...prev,
                  funilObraMotivoPerdaId: e.value
                }))
              }}
              errors={errors.funilObraMotivoPerdaId}
              touched={touched.funilObraMotivoPerdaId}
              name="funilObraMotivoPerdaId"
              spacingControl
              noMargin
            />
          </Field>

          <Field label="Concorrente*:">
            <Select
              options={optionFunilConcorrentes}
              value={optionFunilConcorrentes.find(e => e.value == data.funilObraConcorrenteId)}
              onChange={(e) => {
                setData(prev => ({
                  ...prev,
                  funilObraConcorrenteId: e.value
                }))
              }}
              errors={errors.funilObraConcorrenteId}
              touched={touched.funilObraConcorrenteId}
              name="funilObraConcorrenteId"
              spacingControl
              noMargin
            />
          </Field>

          <Field label="Preço Necessário:" tooltip="Preço necessário para fechar a negociação">
            <input
              className={inputCls}
              value={precoNecessario}
              onChange={(e) => setPreco(formatCurrency(e.target.value))}
              inputMode="numeric"
              placeholder="R$ 0,00"
            />
          </Field>

          <Field label="Prazo de Pagamento (dias):">
            <input
              className={inputCls}
              value={prazoPagamento}
              onChange={(e) => setPrazo(formatDias(e.target.value))}
              inputMode="numeric"
              placeholder="0"
            />
          </Field>

          <div className="flex justify-between gap-3 mt-6">
            <button type="button" onClick={handleClose} className="px-6 py-3 rounded bg-quinaria text-white font-bold">
              Cancelar
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              className="px-6 py-3 rounded bg-secundaria text-primaria font-bold"
            >
              Mapear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LostMappingModal;
